import React from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Autocomplete,
  Checkbox,
  Chip,
} from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox as CheckBoxIcon } from '@mui/icons-material';
import { colors, fonts } from '../../constants';
import { getRegionConfig } from '../../constants/usersPage';

const RegionProjectsSection = ({
  formData,
  projects,
  loadingProjects,
  availableRegions,
  onInputChange,
  onProjectsChange,
}) => {
  const regionConfig = getRegionConfig(formData?.role);
  const isGlobalRole = formData?.role === 'CH' || formData?.role === 'MP';

  const hasRegionSelected = isGlobalRole || (formData?.region && formData.region !== '');
  const isProjectsDisabled = loadingProjects || !hasRegionSelected;

  const selectedProjects = (projects || []).filter((p) =>
    (formData.projects || []).includes(p.value)
  );

  const handleAutocompleteChange = (_event, newValue) => {
    const values = newValue.map((item) => item.value);
    onProjectsChange({ target: { value: values } });
  };

  const handleRemoveChip = (valueToRemove) => {
    const updated = (formData.projects || []).filter((v) => v !== valueToRemove);
    onProjectsChange({ target: { value: updated } });
  };

  const regionHelperText = !formData?.role ? 'Please select a role first' : '';

  return (
    <>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: fonts.weightSemiBold,
          color: colors.textPrimaryDark,
          fontSize: '13px',
          mb: '4px',
          mt: '12px',
        }}
      >
        Region & Projects
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
        {/* Region Field */}
        <TextField
          select={!regionConfig.isFixed}
          disabled={!regionConfig.isVisible || regionConfig.isFixed}
          value={formData.region || (regionConfig.isFixed ? regionConfig.value : '')}
          onChange={onInputChange('region')}
          fullWidth
          size="small"
          placeholder={!formData.role ? 'Select Role First' : 'Select Region'}
          helperText={regionHelperText}
          FormHelperTextProps={{ sx: { fontSize: '11px' } }}
          SelectProps={
            !regionConfig.isFixed
              ? {
                displayEmpty: true,
                sx: { fontSize: '12px' },
                renderValue: (selected) => {
                  if (!selected || selected === '') {
                    return <span style={{ color: colors.textSecondary }}>Select Region</span>;
                  }
                  const match = availableRegions.find((r) => r.value === selected);
                  return match ? match.label : selected;
                },
              }
              : undefined
          }
          InputProps={
            regionConfig.isFixed
              ? {
                readOnly: true,
                sx: { fontSize: '12px', backgroundColor: colors.backgroundLight },
              }
              : undefined
          }
        >
          {!regionConfig.isFixed && (
            <MenuItem value="" disabled sx={{ fontSize: '12px', color: colors.textSecondary }}>
              Select Region
            </MenuItem>
          )}
          {!regionConfig.isFixed &&
            availableRegions.map((region) => (
              <MenuItem key={region.value} value={region.value} sx={{ fontSize: '12px' }}>
                {region.label}
              </MenuItem>
            ))}
        </TextField>

        {/* Projects Autocomplete */}
        <Autocomplete
          multiple
          disableCloseOnSelect
          disabled={isProjectsDisabled}
          options={projects || []}
          value={selectedProjects}
          onChange={handleAutocompleteChange}
          getOptionLabel={(option) => option.label || option.name || ''}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          noOptionsText="No projects available for selected region"
          loading={loadingProjects}
          loadingText="Loading projects..."
          ListboxProps={{
            sx: {
              maxHeight: 260,
              px: '4px',
              '& .MuiAutocomplete-option': {
                fontSize: '13px',
                borderRadius: '6px',
                mx: '4px',
                my: '2px',
                py: '6px',
                px: '10px',
                '&:hover': {
                  backgroundColor: colors.primarySoft,
                },
                '&[aria-selected="true"]': {
                  backgroundColor: colors.primarySoft,
                  '&:hover': {
                    backgroundColor: '#d7ddf3',
                  },
                },
              },
            },
          }}
          componentsProps={{
            paper: {
              sx: {
                borderRadius: '10px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                border: `1px solid ${colors.borderDefault}`,
                mt: '4px',
                overflow: 'hidden',
              },
            },
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.value}>
              <Checkbox
                icon={<CheckBoxOutlineBlank sx={{ fontSize: 18 }} />}
                checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
                checked={selected}
                sx={{
                  mr: 1,
                  p: 0,
                  color: colors.textSecondary,
                  '&.Mui-checked': {
                    color: colors.primary,
                  },
                }}
              />
              <Typography sx={{ fontSize: '13px', color: colors.textPrimary }}>
                {option.label}
              </Typography>
            </li>
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...rest } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option.name || option.label}
                  size="small"
                  onDelete={() => handleRemoveChip(option.value)}
                  {...rest}
                  sx={{
                    fontSize: '11px',
                    height: '24px',
                    borderRadius: '6px',
                    backgroundColor: colors.primarySoft,
                    color: colors.primary,
                    border: `1px solid ${colors.primarySoft}`,
                    '& .MuiChip-deleteIcon': {
                      fontSize: '14px',
                      color: colors.primaryLight,
                      '&:hover': {
                        color: colors.error,
                      },
                    },
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={selectedProjects.length === 0 ? 'Select projects' : ''}
              helperText={
                loadingProjects
                  ? 'Loading projects...'
                  : !hasRegionSelected
                    ? 'Please select a region first'
                    : 'Select one or more projects to assign to this user'
              }
              FormHelperTextProps={{ sx: { fontSize: '11px' } }}
              InputProps={{
                ...params.InputProps,
                sx: {
                  fontSize: '13px',
                  borderRadius: '8px',
                  minHeight: '40px',
                  flexWrap: 'wrap',
                  gap: '4px',
                  py: '4px',
                },
              }}
            />
          )}
        />
      </Box>
    </>
  );
};

export default RegionProjectsSection;
